import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applyColorEffectValue,
  resetColorEffectValue,
} from "../shared/page-color-effects.ts";
import { clampNumber, formatNumber, numberFromConfig } from "../shared/feature-value.ts";

const variable = "--migrosone-a11y-contrast-filter";
const attribute = "data-migrosone-a11y-contrast-mode";
const allowedModeValues = ["off", "high", "inverted"] as const;

type ContrastMode = (typeof allowedModeValues)[number];

const modeLabels: Record<ContrastMode, string> = {
  off: "Normal",
  high: "Yüksek",
  inverted: "Koyu",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readModeList(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === "string");
  }

  if (typeof value === "string") {
    return value.split(",");
  }

  return [];
}

function isContrastMode(value: string): value is ContrastMode {
  return allowedModeValues.includes(value as ContrastMode);
}

function parseContrastConfig(config: unknown) {
  const configuredModes = isRecord(config) ? readModeList(config.allowedModes) : [];
  const allowedModes = configuredModes
    .map((mode) => mode.trim())
    .filter(isContrastMode);
  const uniqueAllowedModes =
    allowedModes.length > 0
      ? Array.from(new Set(allowedModes))
      : [...allowedModeValues];
  const configuredDefault =
    isRecord(config) && typeof config.defaultMode === "string"
      ? config.defaultMode.trim()
      : isRecord(config) && typeof config.mode === "string"
        ? config.mode.trim()
        : "off";
  const defaultMode =
    isContrastMode(configuredDefault) && uniqueAllowedModes.includes(configuredDefault)
      ? configuredDefault
      : uniqueAllowedModes[0] ?? "off";
  const contrast = clampNumber(numberFromConfig(config, "contrast", 140), 100, 200);
  const brightness = clampNumber(numberFromConfig(config, "brightness", 100), 50, 150);

  return {
    allowedModes: uniqueAllowedModes,
    brightness,
    contrast,
    defaultMode,
  };
}

function resolveMode(config: unknown, preference: unknown) {
  const parsed = parseContrastConfig(config);
  if (
    typeof preference === "string" &&
    isContrastMode(preference.trim()) &&
    parsed.allowedModes.includes(preference.trim() as ContrastMode)
  ) {
    return preference.trim() as ContrastMode;
  }

  return parsed.defaultMode;
}

function createFilterValue(config: unknown, mode: ContrastMode) {
  const parsed = parseContrastConfig(config);
  if (mode === "high") {
    return `contrast(${formatNumber(parsed.contrast)}%) brightness(${formatNumber(parsed.brightness)}%)`;
  }

  if (mode === "inverted") {
    return "invert(100%) hue-rotate(180deg)";
  }

  return "";
}

const definition = getFeaturePanelDefinition("contrast-mode");

if (!definition) {
  throw new Error("Missing contrast-mode feature definition");
}

export const contrastModeFeatureModule: RuntimeFeatureModule = {
  key: "contrast-mode",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const parsed = parseContrastConfig(config);
    const wrapper = document.createElement("div");
    const label = document.createElement("label");
    const segmented = document.createElement("div");
    const selected = resolveMode(config, preference);
    const buttons: Array<{ button: HTMLButtonElement; mode: ContrastMode }> = [];

    function syncSelected(nextMode: ContrastMode) {
      for (const entry of buttons) {
        const isSelected = entry.mode === nextMode;
        entry.button.className = isSelected
          ? "migrosone-a11y-widget-feature-segmented__button migrosone-a11y-widget-feature-segmented__button--selected"
          : "migrosone-a11y-widget-feature-segmented__button";
        entry.button.setAttribute("aria-pressed", String(isSelected));
      }
    }

    wrapper.className = "migrosone-a11y-widget-feature-control";
    segmented.className = "migrosone-a11y-widget-feature-segmented";
    segmented.setAttribute("role", "group");
    segmented.setAttribute("aria-label", "Kontrast modu");
    label.textContent = "Kontrast modu";

    for (const mode of parsed.allowedModes) {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = modeLabels[mode];
      button.addEventListener("click", () => {
        syncSelected(mode);
        onPreferenceChange(mode);
      });
      buttons.push({ button, mode });
      segmented.append(button);
    }

    syncSelected(selected);
    wrapper.append(label, segmented);
    return wrapper;
  },
  apply({ config, document, preference }) {
    const mode = resolveMode(config, preference);
    const value = createFilterValue(config, mode);
    if (!value) {
      resetColorEffectValue({ attribute, document, variable });
      return;
    }

    applyColorEffectValue({
      attribute,
      document,
      value,
      variable,
    });
  },
  reset({ config, document }) {
    resetColorEffectValue({ attribute, document, variable });
    const mode = resolveMode(config, undefined);
    const value = createFilterValue(config, mode);
    if (value) {
      applyColorEffectValue({
        attribute,
        document,
        value,
        variable,
      });
    }
  },
};
